import React, { useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  FiArrowLeft,
  FiCalendar,
  FiFileText,
  FiHash,
  FiMail,
  FiMapPin,
  FiPhone,
  FiPlus,
  FiRefreshCw,
  FiTrash2,
  FiUser,
} from 'react-icons/fi';
import { MdDescription, MdPictureAsPdf, MdPreview } from 'react-icons/md';
import InvoiceEditorSection from './components/InvoiceEditorSection';
import InvoiceTemplatePreview from './components/InvoiceTemplatePreview';
import Snackbar from '../Admin-Subscriptions/components/Snackbar';
import {
  computeInvoiceTotals,
  createDefaultInvoiceModel,
  createEmptyLineItem,
  formatMoney,
  generateInvoiceNumber,
} from './utils/invoiceModelDefaults';
import {
  downloadInvoicePdf,
  downloadInvoiceWord,
  invoiceFileBaseName,
} from './utils/invoiceExport';
import {
  fieldSx,
  ghostButtonSx,
  INVOICE_THEME,
  primaryButtonSx,
} from './utils/invoiceDesignTokens';

function updateLineItem(items, id, field, value) {
  return items.map((item) => (item.id === id ? { ...item, [field]: value } : item));
}

export default function CreateInvoicePage() {
  const previewRef = useRef(null);
  const [model, setModel] = useState(createDefaultInvoiceModel);
  const [generating, setGenerating] = useState({ pdf: false, word: false });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'error' });

  const { total } = computeInvoiceTotals(model);
  const patchModel = (fields) => setModel((prev) => ({ ...prev, ...fields }));
  const busy = generating.pdf || generating.word;

  const addLineItem = () => {
    setModel((prev) => ({
      ...prev,
      lineItems: [...prev.lineItems, createEmptyLineItem()],
    }));
  };

  const removeLineItem = (id) => {
    setModel((prev) => ({
      ...prev,
      lineItems: prev.lineItems.length > 1 ? prev.lineItems.filter((item) => item.id !== id) : prev.lineItems,
    }));
  };

  const resetModel = () => setModel(createDefaultInvoiceModel());

  const handleDownloadPdf = async () => {
    setGenerating((prev) => ({ ...prev, pdf: true }));
    try {
      await downloadInvoicePdf(previewRef.current, `${invoiceFileBaseName(model)}.pdf`);
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: err?.message || 'Failed to generate PDF.' });
    } finally {
      setGenerating((prev) => ({ ...prev, pdf: false }));
    }
  };

  const handleDownloadWord = () => {
    setGenerating((prev) => ({ ...prev, word: true }));
    try {
      downloadInvoiceWord(model, `${invoiceFileBaseName(model)}.doc`);
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: err?.message || 'Failed to generate Word document.' });
    } finally {
      setGenerating((prev) => ({ ...prev, word: false }));
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        pb: 6,
        background: `
          radial-gradient(ellipse 80% 50% at 20% -10%, rgba(59,130,246,0.14), transparent),
          radial-gradient(ellipse 60% 40% at 90% 0%, rgba(236,72,153,0.1), transparent),
          #f1f5f9
        `,
      }}
    >
      <Box
        sx={{
          borderBottom: `1px solid ${INVOICE_THEME.border}`,
          background: 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(16px)',
          position: 'sticky',
          top: 72,
          zIndex: 20,
        }}
      >
        <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, md: 3 }, py: 2 }}>
          <Button
            component={RouterLink}
            to="/invoice"
            startIcon={<FiArrowLeft />}
            sx={{ ...ghostButtonSx, mb: 1.5, py: 0.5, minWidth: 0 }}
          >
            Invoice hub
          </Button>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                <Typography
                  component="h1"
                  sx={{
                    fontSize: 'clamp(1.6rem, 3vw, 2.1rem)',
                    fontWeight: 800,
                    letterSpacing: '-0.03em',
                    color: INVOICE_THEME.ink,
                  }}
                >
                  Create Invoice
                </Typography>
                <Chip
                  label="Draft · not saved"
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: 'rgba(59,130,246,0.1)',
                    color: '#2563eb',
                    border: '1px solid rgba(59,130,246,0.2)',
                  }}
                />
              </Box>
              <Typography sx={{ color: INVOICE_THEME.slate, maxWidth: 520, fontSize: '0.92rem' }}>
                Design a polished tax invoice, preview it live, and export as PDF or Word — instantly.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: '14px',
                  bgcolor: '#fff',
                  border: `1px solid ${INVOICE_THEME.border}`,
                  boxShadow: INVOICE_THEME.shadowSoft,
                }}
              >
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: INVOICE_THEME.slate, letterSpacing: '0.08em' }}>
                  LIVE TOTAL
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 800,
                    background: INVOICE_THEME.gradient,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {formatMoney(total, model.currency)}
                </Typography>
              </Box>

              <Button
                sx={primaryButtonSx}
                startIcon={generating.pdf ? <CircularProgress size={16} color="inherit" /> : <MdPictureAsPdf />}
                onClick={handleDownloadPdf}
                disabled={busy}
              >
                PDF
              </Button>
              <Button
                sx={ghostButtonSx}
                variant="outlined"
                startIcon={generating.word ? <CircularProgress size={16} /> : <MdDescription />}
                onClick={handleDownloadWord}
                disabled={busy}
              >
                Word
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, md: 3 }, pt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={5}>
            <Box
              sx={{
                position: { lg: 'sticky' },
                top: { lg: 200 },
                display: 'grid',
                gap: 2,
                maxHeight: { lg: 'calc(100vh - 220px)' },
                overflowY: { lg: 'auto' },
                pr: { lg: 0.5 },
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'rgba(100,116,139,0.35)',
                  borderRadius: 999,
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  startIcon={<FiRefreshCw />}
                  onClick={resetModel}
                  sx={{ ...ghostButtonSx, fontSize: '0.8rem' }}
                >
                  Reset form
                </Button>
              </Box>

              <InvoiceEditorSection
                icon={<FiHash size={18} />}
                title="Document details"
                subtitle="Invoice number, reference, and dates"
              >
                <TextField
                  label="Invoice number"
                  value={model.invoiceNumber}
                  onChange={(e) => patchModel({ invoiceNumber: e.target.value })}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label="Reference"
                  value={model.reference}
                  onChange={(e) => patchModel({ reference: e.target.value })}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <TextField
                    label="Issue date"
                    type="date"
                    value={model.issueDate}
                    onChange={(e) => patchModel({ issueDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={fieldSx}
                  />
                  <TextField
                    label="Due date"
                    type="date"
                    value={model.dueDate}
                    onChange={(e) => patchModel({ dueDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={fieldSx}
                  />
                </Box>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    const next = generateInvoiceNumber();
                    patchModel({ invoiceNumber: next, reference: next });
                  }}
                  sx={{ justifySelf: 'start', textTransform: 'none', fontWeight: 600 }}
                >
                  Generate new number
                </Button>
              </InvoiceEditorSection>

              <InvoiceEditorSection
                icon={<FiUser size={18} />}
                title="Client"
                subtitle="Who receives this invoice"
              >
                <TextField
                  label="Client name"
                  value={model.billToName}
                  onChange={(e) => patchModel({ billToName: e.target.value })}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  placeholder="Hospital or company name"
                />
                <TextField
                  label="Client address"
                  value={model.billToAddress}
                  onChange={(e) => patchModel({ billToAddress: e.target.value })}
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  sx={fieldSx}
                  placeholder="Street, city, state, PIN"
                />
              </InvoiceEditorSection>

              <InvoiceEditorSection
                icon={<FiFileText size={18} />}
                title="Line items"
                subtitle="Products or services billed"
                action={
                  <Button
                    size="small"
                    startIcon={<FiPlus size={14} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      addLineItem();
                    }}
                    sx={{
                      ...ghostButtonSx,
                      py: 0.4,
                      minWidth: 0,
                      fontSize: '0.78rem',
                    }}
                  >
                    Add
                  </Button>
                }
              >
                <Box sx={{ display: 'grid', gap: 1.25 }}>
                  {model.lineItems.map((item, index) => (
                    <Box
                      key={item.id}
                      sx={{
                        p: 1.5,
                        borderRadius: '12px',
                        bgcolor: '#f8fafc',
                        border: `1px solid ${INVOICE_THEME.border}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: INVOICE_THEME.slate }}>
                          ITEM {index + 1}
                        </Typography>
                        <Tooltip title="Remove row">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => removeLineItem(item.id)}
                              disabled={model.lineItems.length <= 1}
                              sx={{ color: '#94a3b8' }}
                            >
                              <FiTrash2 size={14} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <TextField
                        label="Description"
                        value={item.description}
                        onChange={(e) =>
                          setModel((prev) => ({
                            ...prev,
                            lineItems: updateLineItem(prev.lineItems, item.id, 'description', e.target.value),
                          }))
                        }
                        fullWidth
                        size="small"
                        sx={{ ...fieldSx, mb: 1.25 }}
                      />
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                        <TextField
                          label="Qty"
                          type="number"
                          value={item.quantity}
                          onChange={(e) =>
                            setModel((prev) => ({
                              ...prev,
                              lineItems: updateLineItem(prev.lineItems, item.id, 'quantity', e.target.value),
                            }))
                          }
                          size="small"
                          sx={fieldSx}
                          inputProps={{ min: 0, step: '1' }}
                        />
                        <TextField
                          label="Unit price (₹)"
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) =>
                            setModel((prev) => ({
                              ...prev,
                              lineItems: updateLineItem(prev.lineItems, item.id, 'unitPrice', e.target.value),
                            }))
                          }
                          size="small"
                          sx={fieldSx}
                          inputProps={{ min: 0, step: '0.01' }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </InvoiceEditorSection>

              <InvoiceEditorSection
                icon={<FiCalendar size={18} />}
                title="Tax & signature"
                subtitle="GST and authorised signatory"
                defaultOpen={false}
              >
                <TextField
                  label="GST %"
                  type="number"
                  value={model.gstPercent}
                  onChange={(e) => patchModel({ gstPercent: e.target.value })}
                  size="small"
                  sx={fieldSx}
                  inputProps={{ min: 0, step: '0.01' }}
                />
                <TextField
                  label="Issuer signature"
                  value={model.issuerSignature}
                  onChange={(e) => patchModel({ issuerSignature: e.target.value })}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  placeholder="Authorised signatory name"
                />
              </InvoiceEditorSection>

              <InvoiceEditorSection
                icon={<FiMapPin size={18} />}
                title="Company footer"
                subtitle="Contact details on the invoice"
                defaultOpen={false}
              >
                <TextField
                  label="Phone"
                  value={model.companyPhone}
                  onChange={(e) => patchModel({ companyPhone: e.target.value })}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  InputProps={{ startAdornment: <FiPhone size={14} style={{ marginRight: 8, opacity: 0.5 }} /> }}
                />
                <TextField
                  label="Website"
                  value={model.companyWebsite}
                  onChange={(e) => patchModel({ companyWebsite: e.target.value })}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label="Email"
                  value={model.companyEmail}
                  onChange={(e) => patchModel({ companyEmail: e.target.value })}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  InputProps={{ startAdornment: <FiMail size={14} style={{ marginRight: 8, opacity: 0.5 }} /> }}
                />
                <TextField
                  label="Address"
                  value={model.companyAddress}
                  onChange={(e) => patchModel({ companyAddress: e.target.value })}
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  sx={fieldSx}
                />
              </InvoiceEditorSection>
            </Box>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Box
              sx={{
                position: { lg: 'sticky' },
                top: { lg: 200 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      display: 'grid',
                      placeItems: 'center',
                      background: INVOICE_THEME.gradientSoft,
                      color: '#6366f1',
                    }}
                  >
                    <MdPreview size={20} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: INVOICE_THEME.ink, fontSize: '0.95rem' }}>
                      Live preview
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: INVOICE_THEME.slate }}>
                      Updates as you type
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={`${model.lineItems.length} line item${model.lineItems.length === 1 ? '' : 's'}`}
                  size="small"
                  sx={{ fontWeight: 600, bgcolor: '#fff' }}
                />
              </Box>

              <Box
                sx={{
                  p: { xs: 1.5, md: 3 },
                  borderRadius: `${INVOICE_THEME.radiusLg}px`,
                  background: `
                    radial-gradient(circle at 1px 1px, rgba(148,163,184,0.18) 1px, transparent 0)
                  `,
                  backgroundSize: '20px 20px',
                  bgcolor: INVOICE_THEME.canvas,
                  border: `1px solid ${INVOICE_THEME.border}`,
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)',
                }}
              >
                <Box
                  ref={previewRef}
                  sx={{
                    transform: { md: 'scale(1)' },
                    transformOrigin: 'top center',
                    transition: 'transform 0.3s ease',
                  }}
                >
                  <InvoiceTemplatePreview model={model} />
                </Box>
              </Box>

              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  borderRadius: `${INVOICE_THEME.radius}px`,
                  bgcolor: '#fff',
                  border: `1px solid ${INVOICE_THEME.border}`,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  boxShadow: INVOICE_THEME.shadowSoft,
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700, color: INVOICE_THEME.ink }}>
                    Ready to share?
                  </Typography>
                  <Typography sx={{ fontSize: '0.84rem', color: INVOICE_THEME.slate }}>
                    Export a pixel-perfect PDF or editable Word document.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                  <Button
                    sx={{ ...primaryButtonSx, px: 3 }}
                    startIcon={generating.pdf ? <CircularProgress size={16} color="inherit" /> : <MdPictureAsPdf />}
                    onClick={handleDownloadPdf}
                    disabled={busy}
                  >
                    {generating.pdf ? 'Generating…' : 'Download PDF'}
                  </Button>
                  <Button
                    sx={{ ...ghostButtonSx, px: 3 }}
                    variant="outlined"
                    startIcon={generating.word ? <CircularProgress size={16} /> : <MdDescription />}
                    onClick={handleDownloadWord}
                    disabled={busy}
                  >
                    {generating.word ? 'Generating…' : 'Download Word'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Snackbar {...snackbar} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} />
    </Box>
  );
}
